import type { SampleGroup, TrayPath } from '@ctcells/contract'

/** 서버 `recipe_binding.TRAY_LABWARE_TYPE` — 새 형식 레시피에서 챔버 트레이 랩웨어의 종류 이름 */
const TRAY_LABWARE_TYPE = 'hypicc_chamber_tray'
/** 서버 `recipe_v2.compiler.REFERENCE_PREFIX` — 대조 시료 액체 id 머리 */
const REFERENCE_PREFIX = 'REF'

/**
 * **이 레시피의 트레이 자리표** — 옛 형식은 `payload.tray_path` 그대로, 새 형식(Opentrons protocol/8)은 서버와 같은 규칙으로 만든다
 * (서버 `recipe_v2.compiler.sample_loads` · `_layout_groups` · `tray_order` · 2026-10-07).
 *
 * 🔴 왜 필요한가 — 새 형식 레시피엔 `tray_path` 칸이 없다. v0.9.3 서버는 작업 생성 배정을 이 자리표로 재고
 *    (`batches.assignment_problem` → `recipe_v2.records`), 화면은 시료 ↔ 챔버 묶음을 이 자리표로 잇는다
 *    (`sampleGroupFacts` — 없으면 모니터링 카드가 전부 「챔버 배정 없음」이었다 · 2026-10-07 eric 화면).
 *    읽는 곳은 이 함수 하나다 — 개발용 버튼(`quickTrayPath`) · 새 작업 웰 배정 · 모니터링 · 작업 상세.
 * 규칙: 트레이 = `loadLabware` 중 `loadName` 에 트레이 종류 이름이 든 것 · 그 트레이에 싣는 `loadLiquid` 하나가 묶음 하나 ·
 *    `sample_ref` = 액체 id · `role` = id 가 `REF` 로 시작하면 대조 · `wells` = `volumeByWell` 을 적은 차례 · `order` = 묶음 웰을 이은 차례.
 */
export function trayPathOf(payload: unknown): TrayPath | undefined {
  if (typeof payload !== 'object' || payload === null) return undefined
  const doc = payload as { tray_path?: TrayPath; commands?: unknown }
  if (doc.tray_path !== undefined) return doc.tray_path
  if (!Array.isArray(doc.commands)) return undefined
  type Command = { commandType?: unknown; params?: Record<string, unknown> }
  const commands = doc.commands as Command[]
  const tray = commands.find(
    (cmd) =>
      cmd.commandType === 'loadLabware' &&
      typeof cmd.params?.['loadName'] === 'string' &&
      cmd.params['loadName'].includes(TRAY_LABWARE_TYPE),
  )?.params?.['labwareId']
  if (typeof tray !== 'string') return undefined
  const groups: SampleGroup[] = []
  const order: string[] = []
  for (const cmd of commands) {
    if (cmd.commandType !== 'loadLiquid' || cmd.params?.['labwareId'] !== tray) continue
    const liquidId = cmd.params['liquidId']
    const byWell = cmd.params['volumeByWell']
    if (typeof liquidId !== 'string' || typeof byWell !== 'object' || byWell === null) continue
    const wells = Object.keys(byWell)
    groups.push({
      sample_ref: liquidId,
      role: liquidId.startsWith(REFERENCE_PREFIX) ? 'reference' : 'patient',
      wells,
    })
    for (const well of wells) if (!order.includes(well)) order.push(well)
  }
  return groups.length === 0 ? undefined : { order, sample_groups: groups }
}
